import { joinEventGuardLoader } from "routes/loaders/joinEventGuardLoader";
import { eventDetailsGuardLoader } from "routes/loaders/eventDetailsGuardLoader";
import { bookingsGuardLoader } from "routes/loaders/bookingsGuardLoader";
import { sharedBriefcaseGuardLoader } from "routes/loaders/sharedBriefcaseGuardLoader";
import { meetAddonGuardLoader } from "routes/loaders/meetAddonGuardLoader";
import { blockOnLive } from "routes/guards/liveGuard";
import { viewerModeConnectionGuardLoader } from "routes/loaders/viewerModeConnectionGuardLoader";

const usersRoutes = [
  {
    path: "join/:presentationId",
    loader: joinEventGuardLoader,
    lazy: async () => {
      const [joinMod, errorMod] = await Promise.all([
        import("views/User/JoinPresentation"),
        import("views/ErrorPage/EventNotFound"),
      ]);
      const JoinPresentation = joinMod.default;
      const EventNotFound = errorMod.default;
      return { Component: JoinPresentation, ErrorBoundary: EventNotFound };
    },
  },
  {
    path: "waiting-area/",
    lazy: async () => {
      const mod = await import("views/User/WaitingArea");
      const WaitingArea = mod.default;
      return { Component: WaitingArea };
    },
  },
  {
    path: "/meet-addon/side-panel",
    loader: meetAddonGuardLoader,
    lazy: async () => {
      const mod = await import("views/MeetAddon/SidePanel");
      const MeetAddonSidePanel = mod.default;
      return { Component: MeetAddonSidePanel };
    },
  },
  {
    path: "/meet-addon/main-stage",
    loader: meetAddonGuardLoader,
    lazy: async () => {
      const mod = await import("views/MeetAddon/MainStage");
      const MeetAddonMainStage = mod.default;
      return { Component: MeetAddonMainStage };
    },
  },
  {
    path: "/meet-addon/insights",
    loader: meetAddonGuardLoader,
    lazy: async () => {
      try {
        const mod = await import("views/MeetAddon/Insights");
        return { Component: mod.default };
      } catch (error) {
        // Optional route placeholder: keep non-breaking when Insights view is absent.
      }

      const MeetAddonInsightsUnavailable = () => null;
      return { Component: MeetAddonInsightsUnavailable };
    },
  },
  {
    path: "viewer-mode/:friendlyId",
    lazy: async () => {
      const mod = await import("views/User/ViewerMode");
      const ViewerMode = mod.default;
      return { Component: ViewerMode };
    },
  },
  {
    path: "view/:friendlyId",
    loader: viewerModeConnectionGuardLoader,
    lazy: async () => {
      const mod =
        await import("views/User/ViewerMode/user-presentation-screen");
      // @ts-ignore
      const UserPresentationScreen = mod.UserPresentationScreen;
      return { Component: UserPresentationScreen };
    },
  },
  {
    path: "viewer-mode-preview/:friendlyId",
    lazy: async () => {
      const mod =
        await import("views/User/ViewerMode/user-presentation-screen");
      const UserPresentationScreen = mod.UserPresentationScreen;
      return { Component: UserPresentationScreen };
    },
  },
  {
    path: "presentation-ended",
    lazy: async () => {
      const mod =
        await import("views/User/PresentationStatus/PresentationEnded");
      const PresentationEnded = mod.default;
      return { Component: PresentationEnded };
    },
  },
  // Offline presentation access: public lead-capture gate
  {
    path: "docs/:friendlyName/:presentationId",
    lazy: async () => {
      const mod =
        await import("views/post-presenetation/post-presentation-access");
      const PostPresentationAccess = mod.default;
      return { Component: PostPresentationAccess };
    },
  },
  // Offline presentation access: document viewer reached after access is granted
  {
    path: "docs/:friendlyName/:presentationId/view",
    lazy: async () => {
      const mod =
        await import("views/post-presenetation/post-presentation-main");
      const PostPresentationMain = mod.default;
      return { Component: PostPresentationMain };
    },
  },
  {
    path: "removed",
    lazy: async () => {
      const mod = await import("views/User/RemovedUser");
      const RemovedUser = mod.default;
      return { Component: RemovedUser };
    },
  },

  // Accept collaboration invite
  {
    path: "acceptinvite/:friendlyId",
    lazy: async () => {
      const mod = await import("views/User/AcceptTeamInvite");
      const AcceptTeamInvite = mod.default;
      return { Component: AcceptTeamInvite };
    },
  },

  // shared briefcase
  {
    path: "shared-briefcase/:presentationId",
    loader: sharedBriefcaseGuardLoader,
    lazy: async () => {
      const [briefcaseMod, errorMod] = await Promise.all([
        import("views/User/SharedBriefcase"),
        import("views/ErrorPage/SharedBriefcaseNotFound"),
      ]);
      const SharedBriefcase = briefcaseMod.default;
      const SharedBriefcaseNotFound = errorMod.default;
      return {
        Component: SharedBriefcase,
        ErrorBoundary: SharedBriefcaseNotFound,
      };
    },
  },

  // access shared presentation
  {
    path: "access-shared-presentation/:presentationId",
    lazy: async () => {
      const mod = await import("views/User/JoinSharedPresentation");
      const JoinSharedPresentation = mod.default;
      return { Component: JoinSharedPresentation };
    },
  },

  // bookings (absolute path)
  {
    path: "/book",
    loader: bookingsGuardLoader,
    lazy: async () => {
      const [bookingsMod, errorMod] = await Promise.all([
        import("views/User/Bookings"),
        import("views/ErrorPage/BookingNotFound"),
      ]);
      const Bookings = bookingsMod.default;
      const BookingNotFound = errorMod.default;
      return { Component: Bookings, ErrorBoundary: BookingNotFound };
    },
  },
  {
    path: "book/:bookingId",
    loader: bookingsGuardLoader,
    lazy: async () => {
      const [bookMod, errorMod] = await Promise.all([
        import("views/User/Bookings/Book"),
        import("views/ErrorPage/BookingNotFound"),
      ]);
      const Book = bookMod.default;
      const BookingNotFound = errorMod.default;
      return { Component: Book, ErrorBoundary: BookingNotFound };
    },
  },

  // thank you (absolute path)
  {
    path: "/thank-you",
    lazy: async () => {
      const mod = await import("views/User/Post-Event/post-event");
      const EventThankYouPage = mod.EventThankYouPage;
      return { Component: EventThankYouPage };
    },
  },
  {
    path: "/stripe/success",
    lazy: async () => {
      const mod = await import("views/User/StripeSuccess");
      const StripeSuccess = mod.default;
      return { Component: StripeSuccess };
    },
  },
  // Flutterwave returns here after a payment; `?redirect=` carries the page to
  // hand the visitor back to.
  {
    path: "/flutterwave/success",
    lazy: async () => {
      const mod = await import("views/User/FlutterwaveSuccess");
      const FlutterwaveSuccess = mod.default;
      return { Component: FlutterwaveSuccess };
    },
  },

  // event registration / check-in / details
  {
    path: "/event-registration/:eventId",
    lazy: async () => {
      const mod = await import("views/Admin/Events/Event-Registration");
      const EventRegistration = mod.EventRegistration;
      return { Component: EventRegistration };
    },
  },
  {
    path: "/check-in/:eventId",
    lazy: async () => {
      const mod = await import("views/Admin/Events/Event-Check-in");
      const EventCheckIn = mod.EventCheckIn;
      return { Component: EventCheckIn };
    },
  },
  {
    path: "/event-details/:eventId",
    loader: eventDetailsGuardLoader,
    lazy: blockOnLive(async () => {
      const [eventMod, errorMod] = await Promise.all([
        import("components/User/Event/event-user-page"),
        import("views/ErrorPage/EventNotFound"),
      ]);
      const EventUserPage = eventMod.EventUserPage;
      const EventNotFound = errorMod.default;
      return { Component: EventUserPage, ErrorBoundary: EventNotFound };
    }),
  },

  {
    path: "/event-ticket-registration",
    lazy: async () => {
      const mod = await import("components/User/Event/event-user-registration");
      const EventUserRegistration = mod.EventUserRegistration;
      return { Component: EventUserRegistration };
    },
  },

  {
    path: "/event-organiser/:organizerId",
    lazy: async () => {
      const mod =
        await import("components/User/Event/organiser-page/event-organiser-page");
      const EventOrganiserPage = mod.EventOrganiserPage;
      return { Component: EventOrganiserPage };
    },
  },

  // speaker landing
  {
    path: "/speaker",
    lazy: async () => {
      const mod = await import("views/User/SpeakerLandingPage");
      const SpeakerLandingPage = mod.default;
      return { Component: SpeakerLandingPage };
    },
  },

  // {
  //   path: "/emailstudio",
  //   lazy: async () => {
  //     const mod = await import("views/Admin/Bookings/EmailStudio");
  //     const EmailStudio = mod.default;
  //     return { Component: EmailStudio };
  //   },
  // },
];

export default usersRoutes;
